import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button as RSButton,
} from "reactstrap";
import { DefaultInput, Checkbox } from "ui-kit-takeat";
import { uploadBrandImage } from "services/brandsService";
import {
  FormGrid,
  FormRow,
  BrandImagePreview,
  ToggleRow,
} from "./styles";

function formatCnpjMask(value) {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

function toSlug(text) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const emptyForm = {
  fantasy_name: "",
  name: "",
  cnpj: "",
  nfce_token: "",
  is_active: true,
  file_id: null,
};

function BrandFormModal({ isOpen, toggle, brand, onSave, saving, hasNfce }) {
  const [form, setForm] = useState(emptyForm);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (brand) {
        const cnpjDigits = brand.cnpj ? brand.cnpj.replace(/\D/g, "") : "";
        setForm({
          fantasy_name: brand.fantasy_name || "",
          name: brand.name || "",
          cnpj: cnpjDigits ? formatCnpjMask(cnpjDigits) : "",
          nfce_token: brand.nfce_token || "",
          is_active: brand.is_active !== undefined ? brand.is_active : true,
          file_id: brand.file_id || null,
        });
        setImagePreview(brand.file?.url || null);
        setSlugManuallyEdited(true);
      } else {
        setForm(emptyForm);
        setImagePreview(null);
        setSlugManuallyEdited(false);
      }
      setErrors({});
    }
  }, [isOpen, brand]);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const handleFantasyNameChange = useCallback(
    (value) => {
      handleChange("fantasy_name", value);
      if (!slugManuallyEdited) {
        setForm((prev) => ({
          ...prev,
          fantasy_name: value,
          name: toSlug(value),
        }));
      }
    },
    [slugManuallyEdited, handleChange]
  );

  const handleSlugChange = useCallback(
    (value) => {
      setSlugManuallyEdited(true);
      handleChange("name", toSlug(value));
    },
    [handleChange]
  );

  const handleCnpjChange = useCallback(
    (value) => {
      handleChange("cnpj", formatCnpjMask(value));
    },
    [handleChange]
  );

  const handleImageUpload = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setUploadingImage(true);
      try {
        const token = localStorage.getItem("@QRExpress:token");
        const result = await uploadBrandImage(file, token);
        if (result) {
          setForm((prev) => ({ ...prev, file_id: result.id }));
          setImagePreview(result.url);
        }
      } catch (err) {
        console.error("Erro ao fazer upload da imagem:", err);
      } finally {
        setUploadingImage(false);
      }
    },
    []
  );

  const validate = useCallback(() => {
    const newErrors = {};
    if (!form.fantasy_name.trim()) {
      newErrors.fantasy_name = "Nome de exibição é obrigatório";
    }
    if (!form.name.trim()) {
      newErrors.name = "Slug é obrigatório";
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.name)) {
      newErrors.name = "Formato inválido (use letras minúsculas, números e hífens)";
    }
    if (hasNfce) {
      const cnpjDigits = form.cnpj.replace(/\D/g, "");
      if (cnpjDigits && cnpjDigits.length !== 14) {
        newErrors.cnpj = "CNPJ deve ter 14 dígitos";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form, hasNfce]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    onSave(form);
  }, [form, validate, onSave]);

  const isEditing = !!brand;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {isEditing ? "Editar Marca" : "Criar Marca"}
      </ModalHeader>
      <ModalBody>
        <FormGrid>
          <div>
            <span
              style={{
                fontFamily: "Poppins",
                fontSize: 14,
                fontWeight: 500,
                color: "#222",
                marginBottom: 8,
                display: "block",
              }}
            >
              Imagem da marca
            </span>
            <BrandImagePreview hasImage={!!imagePreview}>
              {imagePreview ? (
                <>
                  <img src={imagePreview} alt="Brand" />
                  <label htmlFor="brand-image-upload">
                    {uploadingImage ? "Enviando..." : "Trocar imagem"}
                    <input
                      id="brand-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                  </label>
                </>
              ) : (
                <label htmlFor="brand-image-upload">
                  {uploadingImage ? "Enviando..." : "Selecionar imagem"}
                  <input
                    id="brand-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                </label>
              )}
            </BrandImagePreview>
          </div>

          <FormRow>
            <div>
              <DefaultInput
                title="Nome de exibição *"
                value={form.fantasy_name}
                onChange={(e) => handleFantasyNameChange(e.target.value)}
              />
              {errors.fantasy_name && (
                <span style={{ color: "#C8131B", fontSize: 12, fontFamily: "Poppins" }}>
                  {errors.fantasy_name}
                </span>
              )}
            </div>
            <div>
              <DefaultInput
                title="Slug para URL *"
                value={form.name}
                onChange={(e) => handleSlugChange(e.target.value)}
              />
              {errors.name && (
                <span style={{ color: "#C8131B", fontSize: 12, fontFamily: "Poppins" }}>
                  {errors.name}
                </span>
              )}
              <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#999", marginTop: 2, display: "block" }}>
                Ex: hamburgueria-do-ze
              </span>
            </div>
          </FormRow>

          {hasNfce && (
            <FormRow>
              <div>
                <DefaultInput
                  title="CNPJ"
                  value={form.cnpj}
                  onChange={(e) => handleCnpjChange(e.target.value)}
                  placeholder="XX.XXX.XXX/XXXX-XX"
                />
                {errors.cnpj && (
                  <span style={{ color: "#C8131B", fontSize: 12, fontFamily: "Poppins" }}>
                    {errors.cnpj}
                  </span>
                )}
              </div>
              <DefaultInput
                title="Token NFC-e"
                value={form.nfce_token}
                onChange={(e) => handleChange("nfce_token", e.target.value)}
              />
            </FormRow>
          )}

          <ToggleRow>
            <Checkbox
              label="Marca ativa"
              checked={form.is_active}
              onClick={(e) => handleChange("is_active", e)}
            />
          </ToggleRow>
        </FormGrid>
      </ModalBody>
      <ModalFooter>
        <RSButton color="secondary" onClick={toggle}>
          Cancelar
        </RSButton>
        <RSButton
          color="primary"
          onClick={handleSubmit}
          disabled={saving || uploadingImage}
        >
          {saving ? "Salvando..." : "Salvar"}
        </RSButton>
      </ModalFooter>
    </Modal>
  );
}

export default BrandFormModal;
